import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../identity/types';
import { partnerErrorMessage } from '../state/partnerError';
import { companyPartnersGateway } from '../services';
import { PARTNER_ROLE_LABELS, RELATIONSHIP_STATUS_LABELS } from '../types';
import type { AuthorizedPartner, InvitationPreview, PartnerHistoryEntry, RelationshipStatus } from '../types';
import './authorizedPartners.css';

const OWNER_ONLY_MESSAGE = 'Only a company owner can manage authorized partners.';
const MFA_MESSAGE = 'Recent verification is required. Verify with your authenticator app, then try again.';
/** One message for every way an invitation can be unusable. Never says which, so it cannot be used to probe. */
const INVITATION_UNAVAILABLE_MESSAGE = 'This invitation is not available. It may have expired, been used already, or been sent to a different person.';
const REVOKE_WARNING = 'This takes effect immediately and cannot be undone. The firm would need a new invitation.';
const MIN_REASON = 10;

const STATUS_ICONS: Record<RelationshipStatus, string> = {
  Prospect: '○',
  InvitationSent: '✉',
  AwaitingClientAcceptance: '…',
  Onboarding: '◔',
  Active: '✓',
  Suspended: '⏸',
  Revoked: '✕',
  Ended: '■',
};

type Problem = { kind: 'forbidden' } | { kind: 'message'; message: string };

/** A 403 with the MFA code is "verify again"; any other 403 means the caller is not a company owner. */
function toProblem(err: unknown, fallback: string): Problem {
  if (err instanceof ApiError && err.status === 403) {
    return err.code === 'MFA_REQUIRED' ? { kind: 'message', message: MFA_MESSAGE } : { kind: 'forbidden' };
  }
  return { kind: 'message', message: partnerErrorMessage(err, fallback) };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short' });
}

/** "PartnerClientRelationshipRevoked" -> "Client relationship revoked". */
function readableEvent(eventType: string): string {
  const words = eventType.replace(/^Partner/, '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function readInviteToken(): string | null {
  const hash = typeof window === 'undefined' ? '' : window.location.hash.replace(/^#/, '');
  const token = new URLSearchParams(hash).get('invite');
  return token && token.trim() ? token.trim() : null;
}

/** The secret must leave the address bar and the browser history once it has been used. */
function clearInviteFragment(): void {
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
}

export function AuthorizedPartnersPage() {
  const [partners, setPartners] = useState<AuthorizedPartner[] | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [inviteToken, setInviteToken] = useState<string | null>(readInviteToken);
  // Stable, because the invitation card's preview effect depends on it: an inline arrow would re-run the lookup on every render.
  const handleForbidden = useCallback(() => setForbidden(true), []);

  // Bumping this reloads the list (after an invitation is accepted or declined).
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    companyPartnersGateway.list().then(
      (result) => {
        if (cancelled) return;
        setPartners(result);
        setProblem(null);
      },
      (err: unknown) => {
        if (cancelled) return;
        const p = toProblem(err, 'Could not load authorized partners.');
        if (p.kind === 'forbidden') setForbidden(true);
        else setProblem(p);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  function replacePartner(updated: AuthorizedPartner) {
    setPartners((current) => (current ?? []).map((p) => (p.id === updated.id ? updated : p)));
  }

  if (forbidden) {
    return (
      <main className="ap-page" data-testid="authorized-partners-page">
        <h1>Authorized partners</h1>
        <p role="alert" className="ap-alert">{OWNER_ONLY_MESSAGE}</p>
      </main>
    );
  }

  return (
    <main className="ap-page" data-testid="authorized-partners-page">
      <h1>Authorized partners</h1>
      <p className="ap-intro">
        Accounting and payroll firms you have allowed to work on this company. You decide what each firm may do, and you can reduce, pause or end that access at any time.
      </p>

      {problem?.kind === 'message' && <p role="alert" className="ap-alert">{problem.message}</p>}

      {inviteToken && (
        <InvitationCard
          token={inviteToken}
          onDone={async () => {
            clearInviteFragment();
            setInviteToken(null);
            setReloadKey((k) => k + 1);
          }}
          onForbidden={handleForbidden}
        />
      )}

      {partners === null && !problem && <p className="ap-muted">Loading…</p>}

      {partners !== null && partners.length === 0 && (
        <section className="ap-empty" aria-label="No authorized partners">
          <p><strong>No partner firms are authorized for this company.</strong></p>
          <p className="ap-muted">A firm that wants to work on your company sends you an invitation link. Open it while signed in as a company owner to review what it asks for.</p>
        </section>
      )}

      {partners !== null && partners.length > 0 && (
        <ul className="ap-list" aria-label="Authorized partner firms">
          {partners.map((partner) => (
            <li key={partner.id}>
              <FirmCard partner={partner} onChanged={replacePartner} onForbidden={handleForbidden} />
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function InvitationCard({ token, onDone, onForbidden }: { token: string; onDone: () => Promise<void>; onForbidden: () => void }) {
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await companyPartnersGateway.previewInvitation(token);
        if (cancelled) return;
        setPreview(result);
        setChecked(Object.fromEntries(result.requestedServices.map((s) => [s.code, true])));
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403 && err.code !== 'MFA_REQUIRED') {
          onForbidden();
          return;
        }
        // Whatever went wrong, the owner is told the same thing.
        setUnavailable(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, onForbidden]);

  if (unavailable) {
    return (
      <section className="ap-card ap-invitation" aria-labelledby="ap-invitation-title">
        <h2 id="ap-invitation-title">Invitation</h2>
        <p role="alert" className="ap-alert">{INVITATION_UNAVAILABLE_MESSAGE}</p>
      </section>
    );
  }

  if (!preview) {
    return (
      <section className="ap-card ap-invitation" aria-labelledby="ap-invitation-title">
        <h2 id="ap-invitation-title">Invitation</h2>
        <p className="ap-muted">Checking your invitation…</p>
      </section>
    );
  }

  const selected = preview.requestedServices.filter((s) => checked[s.code]).map((s) => s.code);

  async function run(action: () => Promise<unknown>, fallback: string) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      await onDone();
    } catch (err) {
      const p = toProblem(err, fallback);
      if (p.kind === 'forbidden') onForbidden();
      else setMessage(p.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="ap-card ap-invitation" aria-labelledby="ap-invitation-title">
      <h2 id="ap-invitation-title">Invitation</h2>
      <p>
        <strong>{preview.partnerLegalName}</strong>
        {preview.partnerDba ? <span className="ap-muted"> (doing business as {preview.partnerDba})</span> : null} would like to work on <strong>{preview.clientName}</strong>.
      </p>
      <p className="ap-muted">This invitation is open until {formatDate(preview.invitationExpiresAtUtc)}.</p>

      {message && <p role="alert" className="ap-alert">{message}</p>}

      <fieldset className="ap-fieldset" disabled={busy}>
        <legend>Services you allow (you can allow fewer than the firm asked for)</legend>
        {preview.requestedServices.map((service) => (
          <label key={service.code} className="ap-check">
            <input type="checkbox" checked={!!checked[service.code]} onChange={(e) => setChecked((c) => ({ ...c, [service.code]: e.target.checked }))} />
            <span>{service.label}</span>
          </label>
        ))}
      </fieldset>
      {selected.length === 0 && <p className="ap-muted">Keep at least one service ticked to accept, or decline the invitation.</p>}

      {confirmDecline ? (
        <div role="group" aria-label="Decline invitation" className="ap-confirm">
          <p>Decline this invitation? The firm will not get any access, and the link stops working.</p>
          <div className="ap-actions">
            <button type="button" className="ap-danger" disabled={busy} onClick={() => void run(() => companyPartnersGateway.declineInvitation(token), 'Could not decline the invitation.')}>
              {busy ? 'Declining…' : 'Yes, decline'}
            </button>
            <button type="button" disabled={busy} onClick={() => setConfirmDecline(false)}>Keep invitation</button>
          </div>
        </div>
      ) : (
        <div className="ap-actions">
          <button type="button" className="ap-primary" disabled={busy || selected.length === 0} onClick={() => void run(() => companyPartnersGateway.acceptInvitation(token, selected), 'Could not accept the invitation.')}>
            {busy ? 'Working…' : 'Accept'}
          </button>
          <button type="button" disabled={busy} onClick={() => setConfirmDecline(true)}>Decline</button>
        </div>
      )}
    </section>
  );
}

type Panel = 'suspend' | 'reduce' | 'expiry' | 'revoke' | null;

function FirmCard({ partner, onChanged, onForbidden }: { partner: AuthorizedPartner; onChanged: (updated: AuthorizedPartner) => void; onForbidden: () => void }) {
  const [panel, setPanel] = useState<Panel>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<PartnerHistoryEntry[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const status = partner.status;
  const isLive = status === 'Onboarding' || status === 'Active' || status === 'Suspended';
  const closed = status === 'Revoked' || status === 'Ended';
  const titleId = `ap-firm-${partner.id}`;

  async function run(action: () => Promise<AuthorizedPartner>, fallback: string, done: string) {
    setBusy(true);
    setMessage(null);
    setNotice(null);
    try {
      const updated = await action();
      onChanged(updated);
      setPanel(null);
      setNotice(done);
    } catch (err) {
      const p = toProblem(err, fallback);
      if (p.kind === 'forbidden') onForbidden();
      else setMessage(p.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleHistory() {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && history === null) {
      try {
        setHistory(await companyPartnersGateway.history(partner.id));
        setHistoryError(null);
      } catch (err) {
        const p = toProblem(err, 'Could not load the history.');
        if (p.kind === 'forbidden') onForbidden();
        else setHistoryError(p.message);
      }
    }
  }

  return (
    <article className="ap-card" aria-labelledby={titleId}>
      <header className="ap-card-head">
        <div>
          <h2 id={titleId}>{partner.partnerLegalName}</h2>
          {partner.partnerDba ? <p className="ap-muted">Doing business as {partner.partnerDba}</p> : null}
        </div>
        <span className={`ap-badge ap-badge--${status}`}>
          <span aria-hidden="true">{STATUS_ICONS[status]}</span> {RELATIONSHIP_STATUS_LABELS[status]}
        </span>
      </header>

      {message && <p role="alert" className="ap-alert">{message}</p>}
      {notice && <p role="status" className="ap-notice">{notice}</p>}

      <dl className="ap-facts">
        <div>
          <dt>Allowed services</dt>
          <dd>
            {partner.grantedServices.length === 0 ? '—' : (
              <ul className="ap-chips">
                {partner.grantedServices.map((s) => <li key={s.code}>{s.label}</li>)}
              </ul>
            )}
          </dd>
        </div>
        <div>
          <dt>Access ends</dt>
          <dd>{partner.accessExpiresAtUtc ? formatDate(partner.accessExpiresAtUtc) : 'No end date'}</dd>
        </div>
        <div>
          <dt>People assigned to your company</dt>
          <dd>
            {partner.assignedMembers.length === 0 ? 'Nobody is assigned.' : (
              <ul className="ap-people">
                {partner.assignedMembers.map((m) => (
                  <li key={m.email}>{m.displayName ? `${m.displayName} · ` : ''}{m.email} <span className="ap-muted">({PARTNER_ROLE_LABELS[m.role]})</span></li>
                ))}
              </ul>
            )}
          </dd>
        </div>
        <div>
          <dt>Accepted</dt>
          <dd>{formatDate(partner.acceptedAtUtc)}</dd>
        </div>
        <div>
          <dt>Last reviewed</dt>
          <dd>{formatDate(partner.lastReviewedAtUtc)}</dd>
        </div>
        {status === 'Suspended' && (
          <div>
            <dt>Paused</dt>
            <dd>{formatDate(partner.suspendedAtUtc)}{partner.suspensionReason ? ` — ${partner.suspensionReason}` : ''}</dd>
          </div>
        )}
        {closed && (
          <div>
            <dt>{status === 'Revoked' ? 'Revoked' : 'Ended'}</dt>
            <dd>{formatDate(partner.revokedAtUtc)}{partner.revocationReason ? ` — ${partner.revocationReason}` : ''}</dd>
          </div>
        )}
      </dl>

      {isLive && panel === null && (
        <div className="ap-actions" role="group" aria-label={`Actions for ${partner.partnerLegalName}`}>
          {status === 'Onboarding' && (
            <button type="button" className="ap-primary" disabled={busy} onClick={() => void run(() => companyPartnersGateway.activate(partner.id), 'Could not activate this authorization.', 'Authorization activated.')}>
              {busy ? 'Working…' : 'Activate'}
            </button>
          )}
          {status === 'Suspended' && (
            <button type="button" className="ap-primary" disabled={busy} onClick={() => void run(() => companyPartnersGateway.resume(partner.id), 'Could not resume this authorization.', 'Authorization resumed.')}>
              {busy ? 'Working…' : 'Resume'}
            </button>
          )}
          {(status === 'Active' || status === 'Onboarding') && <button type="button" disabled={busy} onClick={() => setPanel('suspend')}>Suspend</button>}
          <button type="button" disabled={busy} onClick={() => setPanel('reduce')}>Reduce services</button>
          <button type="button" disabled={busy} onClick={() => setPanel('expiry')}>Set expiry</button>
          <button type="button" className="ap-danger" disabled={busy} onClick={() => setPanel('revoke')}>Revoke access</button>
        </div>
      )}

      {panel === 'suspend' && (
        <ReasonPanel
          title="Suspend access"
          intro="While suspended, the firm cannot do anything for this company. You can resume at any time."
          confirmLabel="Suspend access"
          busy={busy}
          onCancel={() => setPanel(null)}
          onConfirm={(reason) => void run(() => companyPartnersGateway.suspend(partner.id, reason), 'Could not suspend this authorization.', 'Authorization suspended.')}
        />
      )}
      {panel === 'revoke' && (
        <ReasonPanel
          title="Revoke access"
          intro={REVOKE_WARNING}
          confirmLabel="Revoke access"
          danger
          busy={busy}
          onCancel={() => setPanel(null)}
          onConfirm={(reason) => void run(() => companyPartnersGateway.revoke(partner.id, reason), 'Could not revoke this authorization.', 'Access revoked.')}
        />
      )}
      {panel === 'reduce' && (
        <ReducePanel
          partner={partner}
          busy={busy}
          onCancel={() => setPanel(null)}
          onSave={(codes) => void run(() => companyPartnersGateway.reduceServices(partner.id, codes), 'Could not reduce the services.', 'Services reduced.')}
        />
      )}
      {panel === 'expiry' && (
        <ExpiryPanel
          current={partner.accessExpiresAtUtc}
          busy={busy}
          onCancel={() => setPanel(null)}
          onSave={(value) => void run(() => companyPartnersGateway.setExpiry(partner.id, value), 'Could not change the expiry.', value ? 'Expiry date saved.' : 'Expiry date removed.')}
        />
      )}

      <div className="ap-history">
        <button type="button" className="ap-link" aria-expanded={historyOpen} aria-controls={`${titleId}-history`} onClick={() => void toggleHistory()}>
          History
        </button>
        {historyOpen && (
          <div id={`${titleId}-history`}>
            {historyError && <p role="alert" className="ap-alert">{historyError}</p>}
            {history === null && !historyError && <p className="ap-muted">Loading…</p>}
            {history !== null && history.length === 0 && <p className="ap-muted">No recorded events yet.</p>}
            {history !== null && history.length > 0 && (
              <ol className="ap-timeline">
                {history.map((entry, index) => (
                  <li key={`${entry.occurredAtUtc}-${index}`}>
                    <strong>{readableEvent(entry.eventType)}</strong>
                    <span className="ap-muted"> · {formatDateTime(entry.occurredAtUtc)}{entry.actorEmail ? ` · ${entry.actorEmail}` : ''}</span>
                    {entry.reason ? <div>Reason: {entry.reason}</div> : null}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function ReasonPanel({ title, intro, confirmLabel, danger = false, busy, onCancel, onConfirm }: {
  title: string; intro: string; confirmLabel: string; danger?: boolean; busy: boolean; onCancel: () => void; onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= MIN_REASON;
  const id = `ap-reason-${title.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div role="group" aria-label={title} className="ap-confirm">
      <h3>{title}</h3>
      <p>{intro}</p>
      <label htmlFor={id}>Reason (at least {MIN_REASON} characters)</label>
      <textarea id={id} rows={3} value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} />
      <div className="ap-actions">
        <button type="button" className={danger ? 'ap-danger' : 'ap-primary'} disabled={busy || !valid} onClick={() => onConfirm(reason.trim())}>
          {busy ? 'Working…' : confirmLabel}
        </button>
        <button type="button" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function ReducePanel({ partner, busy, onCancel, onSave }: { partner: AuthorizedPartner; busy: boolean; onCancel: () => void; onSave: (codes: string[]) => void }) {
  // Only what is allowed today is offered, so the owner can untick but never add. Allowing more takes a new invitation.
  const [checked, setChecked] = useState<Record<string, boolean>>(() => Object.fromEntries(partner.grantedServices.map((s) => [s.code, true])));
  const selected = partner.grantedServices.filter((s) => checked[s.code]).map((s) => s.code);
  const unchanged = selected.length === partner.grantedServices.length;
  return (
    <div role="group" aria-label="Reduce services" className="ap-confirm">
      <h3>Reduce services</h3>
      <p>Untick what this firm should no longer be allowed to do. To allow more later, the firm has to send a new invitation.</p>
      <fieldset className="ap-fieldset" disabled={busy}>
        <legend>Services this firm may keep</legend>
        {partner.grantedServices.map((service) => (
          <label key={service.code} className="ap-check">
            <input type="checkbox" checked={!!checked[service.code]} onChange={(e) => setChecked((c) => ({ ...c, [service.code]: e.target.checked }))} />
            <span>{service.label}</span>
          </label>
        ))}
      </fieldset>
      {selected.length === 0 && <p role="note" className="ap-muted">To remove all of a firm's access, use Revoke access instead.</p>}
      <div className="ap-actions">
        <button type="button" className="ap-primary" disabled={busy || selected.length === 0 || unchanged} onClick={() => onSave(selected)}>
          {busy ? 'Saving…' : 'Save services'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function ExpiryPanel({ current, busy, onCancel, onSave }: { current: string | null; busy: boolean; onCancel: () => void; onSave: (value: string | null) => void }) {
  const [date, setDate] = useState(current ? current.slice(0, 10) : '');
  const today = new Date().toISOString().slice(0, 10);
  const valid = date !== '' && date > today;
  return (
    <div role="group" aria-label="Set expiry" className="ap-confirm">
      <h3>Set expiry</h3>
      <p>After this date the firm's access stops by itself. You can change it again at any time.</p>
      <label htmlFor="ap-expiry-date">Access ends on</label>
      <input id="ap-expiry-date" type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} />
      {date !== '' && !valid && <p role="note" className="ap-muted">Choose a date in the future.</p>}
      <div className="ap-actions">
        <button type="button" className="ap-primary" disabled={busy || !valid} onClick={() => onSave(`${date}T23:59:59.000Z`)}>
          {busy ? 'Saving…' : 'Save expiry'}
        </button>
        <button type="button" disabled={busy} onClick={() => onSave(null)}>No end date</button>
        <button type="button" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
